import {
    CategoryScale,
    Chart as ChartJS,
    ChartOptions,
    Legend,
    LinearScale,
    LineElement,
    LogarithmicScale,
    PointElement,
    TimeScale,
    Title,
    Tooltip as ChartTooltip,
} from "chart.js";
import i18next from "src/i18n/config";

import { Theme } from "@mui/material";

import "chartjs-adapter-luxon";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, LogarithmicScale, Legend, TimeScale, ChartTooltip);

/**
 * Minimum value to avoid log(0)
 */
export const LogZeroBuffer = 0.5;

export const TimeRangeOptions = [
    { label: i18next.t("pages:overviewPage.cards.eventChart.last7Days"), value: 7 },
    { label: i18next.t("pages:overviewPage.cards.eventChart.last14Days"), value: 14 },
    { label: i18next.t("pages:overviewPage.cards.eventChart.last30Days"), value: 30 },
    { label: i18next.t("pages:overviewPage.cards.eventChart.last60Days"), value: 60 },
];

export function getEventColors(theme: Theme) {
    return { notice: theme.palette.emteria.success, warning: theme.palette.emteria.warning };
}

const getAxisTextColor = (theme: Theme) => theme.palette.emteria?.text?.primary ?? theme.palette.text.primary;

const getGridColor = (theme: Theme) => theme.palette.emteria?.border?.secondary ?? "rgba(0, 0, 0, 0.1)";

export function getChartOptions(theme: Theme): ChartOptions<"line"> {
    const axisColor = getAxisTextColor(theme);
    const gridColor = getGridColor(theme);
    return {
        animation: {
            duration: 50,
        },
        scales: {
            x: {
                type: "time" as const,
                time: {
                    unit: "day",
                    tooltipFormat: "cccc, dd LLL yyyy",
                    displayFormats: {
                        day: "d MMM",
                    },
                },
                ticks: { color: axisColor },
                title: { color: axisColor },
                grid: { color: gridColor },
            },
            y: {
                type: "logarithmic" as const,
                min: LogZeroBuffer,
                ticks: {
                    color: axisColor,
                    callback: (value) => {
                        const numericValue = Number(value);
                        if (value === LogZeroBuffer) {
                            return "0";
                        } else if (Number.isInteger(numericValue)) {
                            return value;
                        } else {
                            return "";
                        }
                    },
                },
                title: {
                    display: true,
                    text: i18next.t("pages:overviewPage.cards.eventChart.eventCount"),
                    color: axisColor,
                },
                grid: {
                    display: true,
                    drawTicks: true,
                    // draw grid lines only for integer values
                    color: (context) => {
                        const numericValue = Number(context.tick.value);

                        if (Number.isInteger(numericValue)) {
                            return gridColor;
                        } else {
                            return "transparent";
                        }
                    },
                },
            },
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const raw = context.raw as { x: string | Date; y: number };
                        const value = raw.y === LogZeroBuffer ? 0 : raw.y;
                        return `${context.dataset.label}: ${value}`;
                    },
                },
            },
            legend: {
                display: true,
                position: "top",
                labels: { color: axisColor },
                onHover: (e) => {
                    if (e.native && e.native instanceof MouseEvent && e.native.target instanceof HTMLCanvasElement) {
                        e.native.target.style.cursor = "pointer";
                    }
                },
            },
        },
        elements: {
            point: {
                radius: 4,
                hoverRadius: 8,
            },
            line: {
                borderWidth: 2,
            },
        },
        interaction: {
            mode: "point",
            intersect: true,
        },
        hover: {
            mode: "point",
            intersect: false,
        },
        responsive: true,
        aspectRatio: 2,
        maintainAspectRatio: false,
    };
}

type ChartEventLevel = "notice" | "warning";

export type ClickedPoint = {
    level: ChartEventLevel;
    epochMs: number;
    count: number;
    hasOverlappingEvents: boolean;
};
